/**
 * NPC rules-text tier interpolation.
 *
 * Massif's NPC content embeds per-tier values directly in prose as brace tokens, because one
 * printed stat block serves all three tiers: the Ace's Strafe reads *"Fly {speed} in any
 * direction, automatically dealing {3/4/6} Kinetic damage…"*. Printed raw, a reader sees the
 * braces instead of the numbers; this resolves them against the tier the sheet is showing.
 *
 * **The vocabulary is measured, not guessed** — enumerated across every string field of every
 * `npc_class` / `npc_template` / `npc_feature` record in both shipped packs (`scripts/npc-field-inventory.ts`
 * is the sibling sweep; this one was a one-off scan). 185 occurrences, 29 distinct forms, in
 * exactly three shapes:
 *
 * 1. **Per-tier slash lists — 26 forms, 180 occurrences.** `{3/4/6}`, `{2/3/4}`, `{10/20/30}`.
 *    Pick the tier-th part.
 * 2. **One ragged slash list — `{+0/+2d6/+3d6}`, 2 occurrences.** Same shape, non-numeric parts.
 *    This is why the rule splits on `/` and takes a part **without parsing it as a number** —
 *    a numeric-only rule would silently miss this one and print braces on the Ultra's crit text.
 * 3. **Stat references — `{speed}` / `{SPEED}`, 3 occurrences, v3 only.** Resolved against the
 *    owning class's own `stats`, itself tier-indexed.
 *
 * **An unrecognized token is left verbatim, never blanked.** A token this doesn't understand must
 * stay visible so it reads as obviously-unrendered content rather than silently vanishing — the
 * same honest-degrade posture the pack adapters take with `parked`/`unrouted`/`unlinked`.
 * `npcTierTokens` exists so a test can assert that, over the real packs, the leftover set is empty.
 */

/** The three tiers every NPC stat block and every embedded token is indexed by. */
export const NPC_TIERS = [1, 2, 3] as const;
export type NpcTier = (typeof NPC_TIERS)[number];

/** Clamp any number to a usable tier, so a caller can pass an unvalidated selector value. */
export function asNpcTier(value: number | undefined): NpcTier {
  if (value === 2 || value === 3) return value;
  return 1;
}

/**
 * Resolve one raw stat value at a tier. NPC stats are stored either per-tier (`hp: [10,12,14]`)
 * or as a bare scalar when the stat doesn't scale (`heatcap: 8`) — v2 arrays everything, v3
 * collapses the tier-invariant ones. A scalar is **tier-invariant, not missing**: it reads the
 * same at every tier. A short array is clamped to its last entry rather than reading `undefined`.
 */
export function statAtTier(raw: unknown, tier: NpcTier): number | string | undefined {
  if (typeof raw === "number" || typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return undefined;
    const at = raw[Math.min(tier - 1, raw.length - 1)];
    // v2 stores `size` as a nested per-tier array (`[[1],[1],[1]]`); unwrap one level.
    if (Array.isArray(at)) return typeof at[0] === "number" || typeof at[0] === "string" ? at[0] : undefined;
    return typeof at === "number" || typeof at === "string" ? at : undefined;
  }
  return undefined;
}

/** Every `{…}` token in a string, in order of appearance (duplicates included). */
export function npcTierTokens(text: string): string[] {
  return [...text.matchAll(/\{([^{}]*)\}/g)].map((m) => m[0]);
}

/**
 * Replace every `{…}` token in `text` with its value at `tier`.
 *
 * `stats` is the owning class's raw `stats` object, needed only for shape-3 tokens; omit it and
 * a `{speed}` stays verbatim (which is the honest result for a v2 feature, whose `origin` carries
 * a display *name* rather than a resolvable class id).
 */
export function interpolateTierTokens(
  text: string,
  tier: NpcTier,
  stats?: Record<string, unknown>,
): string {
  if (!text.includes("{")) return text;
  return text.replace(/\{([^{}]*)\}/g, (whole, body: string) => {
    if (body.includes("/")) {
      const parts = body.split("/");
      // Clamp rather than index off the end: a two-part token still resolves at tier 3.
      return (parts[Math.min(tier - 1, parts.length - 1)] ?? "").trim() || whole;
    }
    const key = body.trim().toLowerCase();
    if (key !== "" && stats !== undefined) {
      const resolved = statAtTier(stats[key], tier);
      if (resolved !== undefined) return String(resolved);
    }
    return whole;
  });
}
